import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { Plus, Search, Settings2, Trash2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import { Pagination } from "../../../components/ui/Pagination";
import {
  Table,
  type TableColumn,
} from "../../../components/ui/Table";
import { CrmOrigenSelect } from "./CrmOrigenSelect";

type EstadoOportunidad = "abierta" | "ganada" | "perdida";

interface Oportunidad {
  id: number;
  id_externo: string | null;
  crm_cliente_id: number;
  empresa_id_externo: string | null;
  empresa: string;
  producto_id: number;
  producto: string;
  etapa_id: number;
  etapa: string;
  etapa_codigo: string;
  titulo: string;
  estado: EstadoOportunidad;
  prima_estimada_cents: number | null;
  comision_estimada_cents: number | null;
  probabilidad_bps: number | null;
  fecha_cierre_prevista: string | null;
  motivo_perdida: string | null;
  origen: string | null;
}

interface OportunidadHistorial {
  id: number;
  etapaAnteriorId: number | null;
  etapaNuevaId: number;
  changedByUserId: number | null;
  nota: string | null;
  createdAt: string;
  etapaNueva: string;
}

interface OportunidadDetalle {
  historial: OportunidadHistorial[];
}

interface OportunidadesResponse {
  data: Oportunidad[];
  total: number;
  page: number;
  totalPages: number;
}

interface Empresa {
  id: number;
  idExterno: string | null;
  razonSocial: string;
  estado: "activo" | "inactivo" | "archivado";
}

interface EmpresasResponse {
  data: Empresa[];
}

interface Producto {
  id: number;
  codigo: string;
  nombre: string;
  orden?: number;
  activo?: boolean;
}

interface ProductosResponse {
  data: Producto[];
}

interface Etapa {
  id: number;
  codigo: string;
  nombre: string;
  orden: number;
  resultado: EstadoOportunidad;
  probabilidadBps: number;
}

interface CatalogosResponse {
  productos: Producto[];
  etapas: Etapa[];
}

interface OportunidadForm {
  empresaId: string;
  productoId: string;
  etapaId: string;
  titulo: string;
  primaEstimada: string;
  comisionEstimada: string;
  probabilidad: string;
  fechaCierrePrevista: string;
  origen: string;
  motivoPerdida: string;
}

const EMPTY_FORM: OportunidadForm = {
  empresaId: "",
  productoId: "",
  etapaId: "",
  titulo: "",
  primaEstimada: "",
  comisionEstimada: "",
  probabilidad: "",
  fechaCierrePrevista: "",
  origen: "",
  motivoPerdida: "",
};

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return (
      error.response?.data?.error ??
      "No se pudo completar la operación"
    );
  }

  return "No se pudo completar la operación";
}

function formatMoney(cents: number | null): string {
  if (cents === null) return "—";

  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function estadoBadge(estado: EstadoOportunidad) {
  if (estado === "ganada") {
    return <Badge variant="success">Ganada</Badge>;
  }

  if (estado === "perdida") {
    return <Badge variant="danger">Perdida</Badge>;
  }

  return <Badge variant="info">Abierta</Badge>;
}

function SelectField({
  label,
  value,
  onChange,
  children,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#1A3AB8] focus:outline-none focus:ring-2 focus:ring-[#1A3AB8] disabled:bg-gray-100"
      >
        {children}
      </select>
    </label>
  );
}

export function CrmOportunidadesTab() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const canManageProducts =
    user?.role === "admin" || user?.role === "director_comercial";
  const [oportunidades, setOportunidades] = useState<Oportunidad[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState(() => searchParams.get("estado") ?? "");
  const [etapaId, setEtapaId] = useState("");
  const [etapaCodigos, setEtapaCodigos] = useState(
    () => searchParams.get("etapa_codigos") ?? ""
  );
  const [summaryView, setSummaryView] = useState(
    () => searchParams.get("vista") ?? ""
  );
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Oportunidad | null>(null);
  const [form, setForm] = useState<OportunidadForm>(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [historial, setHistorial] = useState<OportunidadHistorial[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [productsModalOpen, setProductsModalOpen] = useState(false);
  const [allProducts, setAllProducts] = useState<Producto[]>([]);
  const [newProductName, setNewProductName] = useState("");
  const [productsError, setProductsError] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [updatingProductId, setUpdatingProductId] = useState<number | null>(null);

  const selectedStage = useMemo(
    () => etapas.find((etapa) => String(etapa.id) === form.etapaId),
    [etapas, form.etapaId]
  );

  const loadCatalogos = useCallback(async () => {
    try {
      const [catalogosResponse, empresasResponse] = await Promise.all([
        api.get<CatalogosResponse>("/admin/crm/catalogos"),
        api.get<EmpresasResponse>("/admin/crm/clientes", {
          params: { page: 1, limit: 100 },
        }),
      ]);

      setProductos(catalogosResponse.data.productos);
      setEtapas(catalogosResponse.data.etapas);
      setEmpresas(
        empresasResponse.data.data.filter(
          (empresa) => empresa.estado !== "archivado"
        )
      );
    } catch (error) {
      setListError(getErrorMessage(error));
    }
  }, []);

  const loadOportunidades = useCallback(async () => {
    setLoading(true);
    setListError("");

    try {
      const response = await api.get<OportunidadesResponse>(
        "/admin/crm/oportunidades",
        {
          params: {
            page,
            limit: 20,
            search: search.trim() || undefined,
            estado: estado || undefined,
            etapa_id: etapaId || undefined,
            etapa_codigos: etapaCodigos || undefined,
          },
        }
      );

      setOportunidades(response.data.data);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      setOportunidades([]);
      setTotal(0);
      setTotalPages(1);
      setListError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [estado, etapaCodigos, etapaId, page, search]);

  const loadAllProducts = useCallback(async () => {
    setProductsLoading(true);
    setProductsError("");
    try {
      const response = await api.get<ProductosResponse>(
        "/admin/crm/catalogos/productos"
      );
      setAllProducts(response.data.data);
    } catch (error) {
      setProductsError(getErrorMessage(error));
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    const timer = window.setTimeout(loadOportunidades, 250);
    return () => window.clearTimeout(timer);
  }, [loadOportunidades]);

  const openCreate = () => {
    const initialStage = etapas.find((etapa) => etapa.codigo === "NUEVO");
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      etapaId: initialStage ? String(initialStage.id) : "",
      probabilidad: initialStage
        ? String(initialStage.probabilidadBps / 100)
        : "",
    });
    setFormError("");
    setConfirmingDelete(false);
    setHistorial([]);
    setModalOpen(true);
  };

  const openEdit = (oportunidad: Oportunidad) => {
    const opportunityStage = etapas.find(
      (etapa) => etapa.id === oportunidad.etapa_id
    );
    setEditing(oportunidad);
    setForm({
      empresaId: String(oportunidad.crm_cliente_id),
      productoId: String(oportunidad.producto_id),
      etapaId: String(oportunidad.etapa_id),
      titulo: oportunidad.titulo,
      primaEstimada:
        oportunidad.prima_estimada_cents === null
          ? ""
          : String(oportunidad.prima_estimada_cents / 100),
      comisionEstimada:
        oportunidad.comision_estimada_cents === null
          ? ""
          : String(oportunidad.comision_estimada_cents / 100),
      probabilidad:
        opportunityStage?.resultado !== "abierta"
          ? String((opportunityStage?.probabilidadBps ?? 0) / 100)
          : oportunidad.probabilidad_bps === null
          ? ""
          : String(oportunidad.probabilidad_bps / 100),
      fechaCierrePrevista: oportunidad.fecha_cierre_prevista ?? "",
      origen: oportunidad.origen ?? "",
      motivoPerdida: oportunidad.motivo_perdida ?? "",
    });
    setFormError("");
    setConfirmingDelete(false);
    setHistorial([]);
    setModalOpen(true);
    setHistoryLoading(true);
    api
      .get<OportunidadDetalle>(`/admin/crm/oportunidades/${oportunidad.id}`)
      .then((response) => setHistorial(response.data.historial))
      .catch((error: unknown) => setFormError(getErrorMessage(error)))
      .finally(() => setHistoryLoading(false));
  };

  const deleteOpportunity = async () => {
    if (!editing || user?.role !== "admin") return;
    setDeleting(true);
    setFormError("");
    try {
      await api.delete(`/admin/crm/oportunidades/${editing.id}`);
      setModalOpen(false);
      setEditing(null);
      setConfirmingDelete(false);
      setForm(EMPTY_FORM);
      if (page !== 1) setPage(1);
      else await loadOportunidades();
    } catch (error) {
      setFormError(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const closeModal = () => {
    if (saving || deleting) return;
    setModalOpen(false);
    setEditing(null);
    setConfirmingDelete(false);
    setHistorial([]);
    setFormError("");
  };

  const openProductsModal = () => {
    setProductsModalOpen(true);
    setNewProductName("");
    setProductsError("");
    void loadAllProducts();
  };

  const closeProductsModal = () => {
    if (savingProduct || updatingProductId !== null) return;
    setProductsModalOpen(false);
    setNewProductName("");
    setProductsError("");
  };

  const createProduct = async () => {
    const nombre = newProductName.trim();
    if (!nombre) {
      setProductsError("Escribe el nombre del nuevo producto.");
      return;
    }

    setSavingProduct(true);
    setProductsError("");
    try {
      await api.post("/admin/crm/catalogos/productos", { nombre });
      setNewProductName("");
      await Promise.all([loadAllProducts(), loadCatalogos()]);
    } catch (error) {
      setProductsError(getErrorMessage(error));
    } finally {
      setSavingProduct(false);
    }
  };

  const toggleProduct = async (product: Producto) => {
    setUpdatingProductId(product.id);
    setProductsError("");
    try {
      await api.patch(`/admin/crm/catalogos/productos/${product.id}`, {
        activo: !product.activo,
      });
      await Promise.all([loadAllProducts(), loadCatalogos()]);
    } catch (error) {
      setProductsError(getErrorMessage(error));
    } finally {
      setUpdatingProductId(null);
    }
  };

  const setField = (field: keyof OportunidadForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const save = async () => {
    setFormError("");

    if (!form.empresaId || !form.productoId || !form.etapaId) {
      setFormError("Selecciona la empresa, el producto y la fase comercial.");
      return;
    }

    if (selectedStage?.resultado === "perdida" && !form.motivoPerdida.trim()) {
      setFormError("Indica el motivo por el que se pierde la oportunidad.");
      return;
    }

    setSaving(true);
    const payload = {
      crm_cliente_id: Number(form.empresaId),
      producto_id: Number(form.productoId),
      etapa_id: Number(form.etapaId),
      titulo: form.titulo.trim() || undefined,
      prima_estimada: form.primaEstimada || null,
      comision_estimada: form.comisionEstimada || null,
      probabilidad: form.probabilidad || null,
      fecha_cierre_prevista: form.fechaCierrePrevista || null,
      origen: form.origen.trim() || null,
      motivo_perdida:
        selectedStage?.resultado === "perdida"
          ? form.motivoPerdida.trim()
          : null,
    };

    try {
      if (editing) {
        await api.patch(`/admin/crm/oportunidades/${editing.id}`, payload);
      } else {
        await api.post("/admin/crm/oportunidades", payload);
      }

      setModalOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      if (page !== 1) setPage(1);
      else await loadOportunidades();
    } catch (error) {
      setFormError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const columns: TableColumn<Oportunidad>[] = [
    {
      key: "id_externo",
      header: "ID",
      render: (row) => (
        <span className="font-semibold text-[#1A3AB8]">
          {row.id_externo ?? "Generando…"}
        </span>
      ),
    },
    {
      key: "empresa",
      header: "Empresa",
      render: (row) => (
        <div>
          <div className="font-medium">{row.empresa}</div>
          <div className="text-xs text-gray-500">
            {row.empresa_id_externo ?? "Prospect sin ID de cliente"}
          </div>
        </div>
      ),
    },
    { key: "producto", header: "Producto" },
    {
      key: "etapa",
      header: "Fase",
      render: (row) => <Badge variant="neutral">{row.etapa}</Badge>,
    },
    {
      key: "estado",
      header: "Estado",
      render: (row) => estadoBadge(row.estado),
    },
    {
      key: "prima_estimada_cents",
      header: "Prima estimada",
      render: (row) => formatMoney(row.prima_estimada_cents),
    },
    {
      key: "probabilidad_bps",
      header: "Probabilidad",
      render: (row) =>
        row.probabilidad_bps === null
          ? "—"
          : `${row.probabilidad_bps / 100}%`,
    },
    {
      key: "fecha_cierre_prevista",
      header: "Cierre previsto",
      render: (row) => formatDate(row.fecha_cierre_prevista),
    },
    {
      key: "acciones",
      header: "Acciones",
      render: (row) => (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
            Editar
          </Button>
          {user?.role === "admin" && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                openEdit(row);
                setConfirmingDelete(true);
              }}
            >
              <Trash2 className="h-4 w-4" />
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Oportunidades comerciales
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Una empresa puede tener varias oportunidades para productos distintos.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {canManageProducts && (
            <Button variant="secondary" onClick={openProductsModal}>
              <Settings2 className="h-4 w-4" />
              Gestionar productos
            </Button>
          )}
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Nueva oportunidad
          </Button>
        </div>
      </div>

      <div className="grid gap-3 rounded-lg bg-gray-50 p-4 lg:grid-cols-[1fr_200px_240px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            aria-label="Buscar oportunidades"
            placeholder="Buscar por empresa, ID, título o producto"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <select
          aria-label="Filtrar por estado"
          value={estado}
          onChange={(event) => {
            setEstado(event.target.value);
            setPage(1);
          }}
          className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
        >
          <option value="">Todos los estados</option>
          <option value="abierta">Abiertas</option>
          <option value="ganada">Ganadas</option>
          <option value="perdida">Perdidas</option>
        </select>

        <select
          aria-label="Filtrar por fase"
          value={etapaId}
          onChange={(event) => {
            setEtapaId(event.target.value);
            setEtapaCodigos("");
            setSummaryView("");
            setPage(1);
          }}
          className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
        >
          <option value="">Todas las fases</option>
          {etapas.map((etapa) => (
            <option key={etapa.id} value={etapa.id}>
              {etapa.nombre}
            </option>
          ))}
        </select>
      </div>

      {etapaCodigos && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <span>
            Filtro del resumen: {summaryView === "propuestas_pendientes"
              ? "propuestas pendientes"
              : "cotizaciones pendientes"}.
          </span>
          <button
            type="button"
            className="font-medium text-[#1A3AB8] hover:underline"
            onClick={() => {
              setEtapaCodigos("");
              setSummaryView("");
              setPage(1);
            }}
          >
            Quitar filtro
          </button>
        </div>
      )}

      {listError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {listError}
        </div>
      )}

      <div className="text-sm text-gray-600">
        {total === 1 ? "1 oportunidad" : `${total} oportunidades`}
      </div>

      <Table
        columns={columns}
        data={oportunidades}
        loading={loading}
        emptyMessage="Todavía no hay oportunidades comerciales"
        floatingHorizontalScrollbar
      />

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editing ? "Editar oportunidad" : "Nueva oportunidad"}
        size="lg"
        footer={
          <>
            {editing && user?.role === "admin" && !confirmingDelete && (
              <Button
                variant="danger"
                onClick={() => setConfirmingDelete(true)}
                disabled={saving || deleting}
              >
                <Trash2 className="h-4 w-4" />
                Eliminar oportunidad
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={closeModal}
              disabled={saving || deleting}
            >
              Cancelar
            </Button>
            <Button onClick={save} loading={saving} disabled={deleting}>
              {editing ? "Guardar cambios" : "Crear oportunidad"}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {formError}
            </div>
          )}

          {confirmingDelete && editing && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-4">
              <p className="font-semibold text-red-900">
                ¿Eliminar definitivamente esta oportunidad?
              </p>
              <p className="mt-1 text-sm text-red-800">
                Se eliminará también su historial de fases. La acción no se puede
                deshacer y quedará bloqueada si la oportunidad está vinculada a una póliza.
              </p>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                >
                  No eliminar
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={deleteOpportunity}
                  loading={deleting}
                >
                  Eliminar definitivamente
                </Button>
              </div>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <SelectField
              label="Empresa *"
              value={form.empresaId}
              onChange={(value) => setField("empresaId", value)}
              disabled={Boolean(editing)}
            >
              <option value="">Selecciona una empresa</option>
              {empresas.map((empresa) => (
                <option key={empresa.id} value={empresa.id}>
                  {empresa.razonSocial}
                  {empresa.idExterno ? ` · ${empresa.idExterno}` : ""}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Producto *"
              value={form.productoId}
              onChange={(value) => setField("productoId", value)}
            >
              <option value="">Selecciona un producto</option>
              {editing &&
                !productos.some((producto) => producto.id === editing.producto_id) && (
                  <option value={editing.producto_id}>
                    {editing.producto} (inactivo)
                  </option>
                )}
              {productos.map((producto) => (
                <option key={producto.id} value={producto.id}>
                  {producto.nombre}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Fase comercial *"
              value={form.etapaId}
              onChange={(value) => {
                const etapa = etapas.find((item) => String(item.id) === value);
                setForm((current) => ({
                  ...current,
                  etapaId: value,
                  probabilidad: etapa
                    ? String(etapa.probabilidadBps / 100)
                    : "",
                  motivoPerdida:
                    etapa?.resultado === "perdida"
                      ? current.motivoPerdida
                      : "",
                }));
              }}
            >
              <option value="">Selecciona una fase</option>
              {etapas.map((etapa) => (
                <option key={etapa.id} value={etapa.id}>
                  {etapa.nombre}
                </option>
              ))}
            </SelectField>

            <Input
              label="Título"
              placeholder="Se genera automáticamente si lo dejas vacío"
              value={form.titulo}
              onChange={(event) => setField("titulo", event.target.value)}
            />

            <Input
              label="Prima estimada (€)"
              type="number"
              min="0"
              step="0.01"
              value={form.primaEstimada}
              onChange={(event) => setField("primaEstimada", event.target.value)}
            />

            <Input
              label="Comisión estimada (€)"
              type="number"
              min="0"
              step="0.01"
              value={form.comisionEstimada}
              onChange={(event) => setField("comisionEstimada", event.target.value)}
            />

            <Input
              label="Probabilidad (%)"
              type="number"
              min="0"
              max="100"
              step="1"
              value={form.probabilidad}
              onChange={(event) => setField("probabilidad", event.target.value)}
              disabled={selectedStage?.resultado !== "abierta"}
            />

            <Input
              label="Fecha de cierre prevista"
              type="date"
              value={form.fechaCierrePrevista}
              onChange={(event) =>
                setField("fechaCierrePrevista", event.target.value)
              }
            />

            <CrmOrigenSelect
              className="md:col-span-2"
              value={form.origen}
              onChange={(value) => setField("origen", value)}
            />

            {selectedStage?.resultado === "perdida" && (
              <div className="md:col-span-2">
                <Input
                  label="Motivo de pérdida *"
                  value={form.motivoPerdida}
                  onChange={(event) =>
                    setField("motivoPerdida", event.target.value)
                  }
                />
              </div>
            )}
          </div>

          {editing && (
            <section className="border-t border-gray-200 pt-5">
              <h3 className="font-semibold text-gray-900">
                Historial de fases
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Registro cronológico de la evolución de esta oportunidad.
              </p>

              {historyLoading ? (
                <p className="py-6 text-center text-sm text-gray-500">
                  Cargando historial…
                </p>
              ) : historial.length === 0 ? (
                <p className="mt-3 rounded-lg bg-gray-50 py-6 text-center text-sm text-gray-500">
                  No hay cambios de fase registrados.
                </p>
              ) : (
                <ol className="mt-4 space-y-3 border-l-2 border-blue-100 pl-4">
                  {historial.map((item) => (
                    <li key={item.id} className="relative">
                      <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#1A3AB8]" />
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="info">{item.etapaNueva}</Badge>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(item.createdAt)}
                        </span>
                      </div>
                      {item.nota && (
                        <p className="mt-1 text-sm text-gray-600">{item.nota}</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={productsModalOpen}
        onClose={closeProductsModal}
        title="Gestionar productos"
        size="lg"
        footer={
          <Button variant="secondary" onClick={closeProductsModal}>
            Cerrar
          </Button>
        }
      >
        <div className="space-y-5">
          <div>
            <p className="text-sm text-gray-600">
              Añade nuevos tipos de póliza o desactiva los que ya no se ofrezcan.
              Las oportunidades y pólizas históricas conservarán siempre su producto.
            </p>
          </div>

          {productsError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {productsError}
            </div>
          )}

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">
                Nuevo producto
              </span>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  placeholder="Ejemplo: Responsabilidad civil de eventos"
                  value={newProductName}
                  maxLength={100}
                  onChange={(event) => setNewProductName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void createProduct();
                    }
                  }}
                />
                <Button onClick={createProduct} loading={savingProduct}>
                  <Plus className="h-4 w-4" />
                  Añadir producto
                </Button>
              </div>
            </label>
          </div>

          <div className="space-y-2">
            {productsLoading ? (
              <p className="py-6 text-center text-sm text-gray-500">
                Cargando productos…
              </p>
            ) : allProducts.length === 0 ? (
              <p className="rounded-lg bg-gray-50 py-6 text-center text-sm text-gray-500">
                No hay productos configurados.
              </p>
            ) : (
              allProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{product.nombre}</div>
                    <div className="mt-1">
                      <Badge variant={product.activo ? "success" : "neutral"}>
                        {product.activo ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={updatingProductId === product.id}
                    disabled={updatingProductId !== null && updatingProductId !== product.id}
                    onClick={() => toggleProduct(product)}
                  >
                    {product.activo ? "Desactivar" : "Activar"}
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
